import ua from '@tencent/docs-user-agent';
import React from 'react';
import { setNewWindowUrl } from '@tencent/tencent_doc_open_url';
import i18n from '@i18next/i18n';
import config, { EnterPriseType } from '@src/js/service/enterprise/temp-config';
import Loading from '../loading';
import { MEETING_ROOM_STATE } from '../../message_const';
import * as icons from '../../img/svg';
import entLogo from '../../img/ent_logo.png';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import stylesMobile from '../../style-mobile.less';
import stylesPC from '../../style-pc.less';

const styles = browser.isMobile ? stylesMobile : stylesPC;

const meetingRoomInfoMap = {
    [MEETING_ROOM_STATE.INIT_LOADING]: {
        loading: i18n.t('加载中......'),
        showPic: false,
        showLoading: true,
    },
    [MEETING_ROOM_STATE.PLAYER_LOADING]: {
        showPic: true,
        picClass: styles.waiting,
        h1: i18n.t('加载中......'),
        h2: i18n.t('请稍候'),
        showLoading: false,
    },
    [MEETING_ROOM_STATE.CLOSED]: {
        picClass: styles.waiting,
        h1: i18n.t('远程演示已结束'),
        h2: i18n.t('请关闭页面'),
        showPic: true,
        showLoading: false,
    },
    [MEETING_ROOM_STATE.ROOM_CLOSE]: {
        picClass: styles.waiting,
        h1: i18n.t('远程演示已结束'),
        h2: i18n.t('请关闭页面'),
        showPic: true,
        showLoading: false,
    },
    [MEETING_ROOM_STATE.ROOM_FULL]: {
        picClass: styles.waiting,
        h1: i18n.t('未成功加入演示'),
        h2: i18n.t('接入人数已超过上限'),
        showPic: true,
        showLoading: false,
    },
};

const getNotPlayingInfo = ({
    meetingRoomState, typeName, lastError, docTitle, isMaster,
}) => {
    switch (meetingRoomState) {
        case MEETING_ROOM_STATE.AUDIENCE_WAIT:
            return {
                picClass: styles.waiting,
                h1: docTitle,
                h2: isMaster ? i18n.t('加载中......') : `${i18n.t('等待演示者播放')}${typeName}`,
                showPic: true,
                showLoading: false,
            };
        case MEETING_ROOM_STATE.AUDIENCE_LOADING:
            return {
                picClass: styles.waiting,
                h1: i18n.t('远程演示暂未开始'),
                h2: `${i18n.t('开始播放后，将同步显示')}${typeName}`,
                loading: `${i18n.t('演示已开始，正在加载')}${typeName}...`,
                showPic: false,
                showLoading: true,
            };
        case MEETING_ROOM_STATE.ERROR_PAGE:
            return {
                picClass: styles.waiting,
                h1: i18n.t('😰糟糕，出错了！'),
                h2: `${i18n.t('请稍后刷新页面')}(${lastError})`,
                showPic: true,
                showLoading: false,
            };
        case MEETING_ROOM_STATE.PLAY:
            return {
                picClass: styles.waiting,
                h1: i18n.t('播放启动中'),
                h2: `${i18n.t('请稍候')}(${meetingRoomState})`,
                showPic: true,
                showLoading: false,
            };
        default:
            return meetingRoomInfoMap[meetingRoomState] || {
                picClass: styles.waiting,
                h1: i18n.t('服务器开小差了'),
                h2: `${i18n.t('请刷新稍后刷新页面')}(${meetingRoomState}, ${window?.clientVars?.__retcode})`,
                showPic: true,
                showLoading: false,
            };
    }
};

const onLogoClick = () => {
    setNewWindowUrl(window, `https://${window.hostname}`);
};

export const HeaderLogo = ({ onClick = onLogoClick }: { onClick?: () => void }) => {
    if (config.supportEnterprise(EnterPriseType.ALL)) {
        return (<div className={styles.entLogoWrapper} onClick={onClick}>
            <img className={styles.entLogo} src={entLogo}></img>
        </div >);
    }
    return (<div className={styles.headerLogoWrapper} onClick={onClick}>
        <img className={styles.headerLogo} src={icons.LOGO}></img>
    </div >);
};

interface INotPlayingContainerProps {
    meetingRoomState?: MEETING_ROOM_STATE;
    logo: string;
    typeName: string;
    lastError: string;
    docTitle: string;
    isMaster: boolean;
}

const NotPlayingContainer = (props: INotPlayingContainerProps) => {
    const { meetingRoomState, logo, typeName, lastError, docTitle, isMaster } = props;
    if (meetingRoomState === MEETING_ROOM_STATE.PLAY) {
        return null;
    }
    const {
        picClass, h1, h2, loading, showLoading, showPic,
    } = getNotPlayingInfo({
        meetingRoomState, typeName, lastError, docTitle, isMaster,
    });
    const showLogo = !ua.isTencentMeeting;
    return <div className={styles.notPlayingContainer}>
        {showLogo && <HeaderLogo />}
        {showPic && <div className={`${styles.pic} ${picClass}`}></div>}
        {showPic
            && meetingRoomState === MEETING_ROOM_STATE.AUDIENCE_WAIT
            ? <div className={styles.info}>
                <div className={styles.title}>
                    <img className={styles.logo} src={logo}></img>
                    <span className={styles.h}>{h1}</span>
                </div>
                <h2 className={styles.waitingH2}>{h2}</h2>
            </div>
            : <div className={styles.info}>
                <h1>{h1}</h1>
                <h2>{h2}</h2>
            </div>
        }
        <Loading showLoading={showLoading} loading={loading} />
    </div>;
};

export default NotPlayingContainer;
